import { useEffect, useRef, useState } from 'react';
import { useDebounced } from './useDebounced';

/**
 * A search box whose text lives in the address (`?q=`). The box updates on
 * every keystroke and the address catches up once the text settles, so a shared
 * link carries the search that produced the screen without a history entry per
 * letter.
 *
 * It writes only when the settled TEXT changes, and an address changed from
 * outside the box (Back, a saved view, the nav leaf clicked while already on
 * the list) puts its value into the box. Comparing the settled text with the
 * address instead would write the old text straight back over the navigation.
 */
export function useSearchBox(value: string, commit: (next: string) => void, delayMs = 300) {
  const [raw, setRaw] = useState(value);
  const debounced = useDebounced(raw, delayMs);
  const settled = useRef(debounced);
  const written = useRef(value);

  useEffect(() => {
    if (debounced === settled.current) return;
    settled.current = debounced;
    if (debounced !== value) {
      written.current = debounced;
      commit(debounced);
    }
  }, [debounced, value, commit]);

  useEffect(() => {
    if (value === written.current) return;
    written.current = value;
    setRaw(value);
  }, [value]);

  return [raw, setRaw] as const;
}
